// /admin/bonds — list of every bond in the system, sorted by most
// recent letter activity. Click → /admin/bonds/$id (inspector page).

import { Link, createFileRoute } from '@tanstack/react-router';
import { type AdminBondListRow, listAdminBondsForInspector } from '../lib/api';

export const Route = createFileRoute('/admin/bonds/')({
  loader: async () => {
    const bonds = await listAdminBondsForInspector();
    return { bonds };
  },
  component: BondsListPage,
});

// Note: the Route id has a trailing slash (`/admin/bonds/`) — this is
// the TanStack flat-routes index marker. The PATH is still
// `/admin/bonds` (no trailing slash) per the parent admin.bonds.tsx
// layout file pattern. The detail route admin.bonds.$id.tsx renders
// inside the parent's <Outlet />.

function fmtAgo(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

function BondsListPage() {
  const { bonds } = Route.useLoaderData();
  return (
    <section>
      <header className="mb-6">
        <h2 className="font-display text-2xl text-ash-50 tracking-wide">Bonds</h2>
        <p className="mt-1 font-body text-ash-500 text-sm italic">
          Every bond in the system. Click into one to inspect its current state, thread,
          memories, and bond soul.
        </p>
      </header>

      {bonds.length === 0 ? (
        <p className="font-body text-ash-300">No bonds yet.</p>
      ) : (
        <ul className="space-y-2">
          {bonds.map((b) => (
            <BondListRow key={b.id} bond={b} />
          ))}
        </ul>
      )}
    </section>
  );
}

function BondListRow({ bond }: { bond: AdminBondListRow }) {
  return (
    <li>
      <Link
        to="/admin/bonds/$id"
        params={{ id: bond.id }}
        className="block rounded-sm border border-paper-800/60 bg-paper-900/30 p-4 transition hover:border-accent/50 hover:bg-paper-800/40"
      >
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-display text-ash-100 text-base">{bond.emberName}</span>
          <span className="font-display text-[0.6rem] text-ash-500 uppercase tracking-[0.32em]">
            {bond.primaryElement}
          </span>
          {bond.relationshipState && (
            <span className="font-display text-[0.6rem] text-accent uppercase tracking-[0.32em]">
              {bond.relationshipState}
            </span>
          )}
          {bond.userEmail && (
            <span className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.3em]">
              {bond.userEmail}
            </span>
          )}
          <span className="ml-auto font-display text-[0.58rem] text-ash-400">
            {bond.letterCount} letter{bond.letterCount === 1 ? '' : 's'} · last{' '}
            {fmtAgo(bond.lastLetterAt)}
          </span>
        </div>
      </Link>
    </li>
  );
}
